import React from 'react';
import clsx from 'clsx';
import { useIntl } from 'react-intl';
import { Search } from 'semantic-ui-react';
import { useNavigate } from 'react-router-dom';
import useDebounce from '@/utils/debounce';
import { hasMinimumLength } from '@/utils/resultsHelper';
import { states } from '@/constants/lifecycle';
import RoleEvents from '@/roles/hooks/events';
import ConfigurationEvents from '@/configurations/hooks/events';
import TokenEvents from '@/tokens/hooks/events';
import AboutEvents from '@/app/hooks/events';
import { advancedSearchResult, kbResults, searchResults } from '../typeAheadSearch';
import { actionResults } from '../typeAheadSearchActions';
import { linksResults, urlResults } from '../typeAheadSearchUrls';
import Selectors from '../hooks/selectors';
import Events from '../hooks/events';

const OmniSearch = ({ category, placeholder, showNoResults }) => {
  const intl = useIntl();
  const navigate = useNavigate();
  const state = Selectors.useOmniSearch({ category });
  const updateSearch = Events.useUpdateSearchProperties(category, { code: null });
  const search = Events.usePerformFind(category, { code: null });
  const about = AboutEvents.useShowAbout();
  const newRole = RoleEvents.useShowRole(null, { code: null });
  const defaultRole = RoleEvents.useShowDefaultRole();
  const defaultApproval = ConfigurationEvents.useShowAutoApproval();
  const newToken = TokenEvents.useShowSaveToken(null, { code: null });

  const debounceSearch = useDebounce((args) => search(args));
  const title = placeholder || intl.formatMessage({ id: 'fair.search' });

  const handleSearchChange = (value) => {
    if (value.length !== 0 && state.userPermissions.search.run
      && state.userPermissions.datasets.read && state.showDatasets) {
      updateSearch({ properties: { query: value } });
      debounceSearch({ page: 1 });
    } else {
      updateSearch({ properties: { query: value, results: [] } });
    }
  };

  const mergedResults = () => {
    if (!hasMinimumLength(state.query)) {
      return [];
    }

    const actions = state.showLinks ? actionResults(
      state,
      intl,
      about,
      newRole,
      defaultRole,
      defaultApproval,
      newToken,
    ) : null;

    const searchState = { ...state, linksName: state.searchLinksName, links: state.searchLinks };
    const allLinks = state.showLinks ? {
      ...linksResults(navigate, searchState, intl, false),
      ...linksResults(navigate, state, intl),
      ...urlResults(navigate, state, intl),
      ...actions,
    } : null;

    const datasets = state.showDatasets ? searchResults(navigate, state, intl) : null;
    const kbLink = state.showKbLink ? kbResults(state, intl) : null;
    const allResults = { ...kbLink, ...allLinks, ...datasets };
    const categoryKey = state.categorise ? null : Object.keys(allResults)[0];
    const categorySearchResults = allResults[categoryKey]?.results
      ? [...allResults[categoryKey].results, advancedSearchResult(navigate, state, intl)] : null;

    return categoryKey ? categorySearchResults : allResults;
  };

  return (
    <Search
      className={clsx('aridhia-search-field navbar-search aridhia-navbar-search', `aridhia-navbar-search--${category}`)}
      title={title}
      category={state.categorise}
      selectFirstResult
      showNoResults={showNoResults}
      onResultSelect={(e, data) => { data.result.onClick(); }}
      loading={state.searchStatus === states.PENDING}
      onSearchChange={(e, { value }) => handleSearchChange(value)}
      results={mergedResults()}
      value={state.query}
      input={{ maxLength: 50, icon: 'search', placeholder: title }}
    />
  );
};

export default OmniSearch;
